import React, { useState } from 'react';
import CommonModal from '../../Components/CommonModal';
import { add_new_vendor_name } from '../../services';
import { SERVICE_OPTIONS, CATEGORY_OPTIONS, YES_NO_OPTIONS, GRADE_COLORS, criticalityTone } from '../tpcrmUtils';
import '../../styles/TpcrmPortal.scss';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const NAME_REGEX = /^[a-zA-Z0-9\s]+$/;

const DEFAULT_VALUES = {
    vendorName: '',
    vendorDomain: '',
    vendorEmail: '',
    serviceProvided: '',
    credentialAccess: false,
    customerPersonalData: false,
    customerSensitivePersonalData: false,
    customerFinancialData: false,
    customerHealthData: false,
    customerAuthData: false,
    businessConfidentialData: false,
    vendorCategory: 'Auto',
    internalOwner: '',
};

// Config-driven fields (CommonModal's `fields=`/`defaultValues=`/`onSubmit=`
// mode, same pattern AIDsar.js uses) instead of hand-managed useState per
// field. One behavior change from the previous manual version: Vendor Name
// used to auto-strip invalid characters and auto-capitalize as you type —
// `fields=` has no keystroke-transform hook, only post-submit `validate`, so
// invalid characters are now caught on submit instead of silently rewritten
// (accepted tradeoff — matches how every other fields=-based form in the app
// already validates rather than auto-corrects). The Vendor Category "manually
// set" hint (shown only when the value isn't "Auto") is dropped for the same
// reason: it depended on watching the field's live value, which `fields=`
// doesn't expose back to the caller — a cosmetic loss, not a validation one.
const FIELDS = [
    {
        name: 'vendorName', label: 'Vendor Name', required: 'Vendor name is required.', half: true,
        maxLength: { value: 255, message: 'Vendor name must be less than 255 characters.' },
        validate: (v) => NAME_REGEX.test(v) || 'Vendor name can only contain letters, numbers and spaces.',
    },
    {
        name: 'vendorDomain', label: 'Vendor Domain', required: 'Vendor domain is required.', half: true,
        maxLength: { value: 255, message: 'Vendor domain must be less than 255 characters.' },
    },
    {
        name: 'vendorEmail', label: 'Vendor Email', type: 'email', required: 'Vendor email is required.', half: true,
        pattern: { value: EMAIL_REGEX, message: 'Please enter a valid email address.' },
    },
    {
        name: 'serviceProvided', label: 'Service Provided', type: 'select', required: 'Service provided is required.',
        half: true, options: SERVICE_OPTIONS, placeholder: 'Select Service',
    },
    { name: 'credentialAccess', label: 'Credential Access', type: 'select', half: true, options: YES_NO_OPTIONS },
    { name: 'customerPersonalData', label: 'Customer Personal Data', type: 'select', half: true, options: YES_NO_OPTIONS },
    { name: 'customerSensitivePersonalData', label: 'Customer Sensitive Personal Data', type: 'select', half: true, options: YES_NO_OPTIONS },
    { name: 'customerFinancialData', label: 'Customer Financial Data', type: 'select', half: true, options: YES_NO_OPTIONS },
    { name: 'customerHealthData', label: 'Customer Health Data', type: 'select', half: true, options: YES_NO_OPTIONS },
    { name: 'customerAuthData', label: 'Customer Auth Data', type: 'select', half: true, options: YES_NO_OPTIONS },
    { name: 'businessConfidentialData', label: 'Business Confidential Data', type: 'select', half: true, options: YES_NO_OPTIONS },
    { name: 'vendorCategory', label: 'Vendor Category', type: 'select', half: true, options: CATEGORY_OPTIONS },
    {
        name: 'internalOwner', label: 'Internal Owner', half: true,
        validate: (v) => {
            if (!v) return true;
            if (!EMAIL_REGEX.test(v)) return 'Please enter a valid internal owner email address.';
            if (v.length > 255) return 'Internal owner must be less than 255 characters.';
            return true;
        },
    },
];

// ── Grading guide shown beside the form ─────────────────────────────────
// A quiet explainer, not a live preview — it doesn't read the form values. It
// just helps whoever is adding a vendor picture what their answers feed into:
// sensitive data raises the vendor's criticality, the service they run sets a
// starting point, and the assessment score decides where the A–F grade finally
// settles. Mirrors the criticality / grade logic in the TPCRM scoring sheet.
// Styling lives in TpcrmPortal.scss (.tpcrm-guide*); only the grade /
// criticality colours are applied inline via the tpcrmUtils helpers, same as
// GradeDistributionBars.js.
const SENSITIVITY_TIERS = [
    { tier: 'Critical', desc: 'Credential access, sensitive personal or health data' },
    { tier: 'High', desc: 'Financial or authentication data' },
    { tier: 'Medium', desc: 'Personal (PII) or business-confidential data' },
];

// Criticality × Grade risk matrix (mirrors the TPCRM risk-heatmap sheet).
const MATRIX_CRIT = ['Critical', 'High', 'Medium', 'Low'];      // rows, worst → best (top-down)
const MATRIX_GRADE = ['A', 'B', 'C', 'D', 'F'];                 // cols, best → worst
// Combined risk band = criticality rank (Low 0 → Critical 3) + grade rank
// (A 0 → F 4). Higher sum → hotter cell. Returns a criticality label reused
// for its tone via criticalityTone().
const cellTier = (crit, grade) => {
    const sum = (3 - MATRIX_CRIT.indexOf(crit)) + MATRIX_GRADE.indexOf(grade);   // 0 … 7
    if (sum <= 1) return 'Low';
    if (sum <= 3) return 'Medium';
    if (sum <= 5) return 'High';
    return 'Critical';
};
const RISK_BANDS = ['Low', 'Medium', 'High', 'Critical'];

function VendorGradingGuide() {
    return (
        <aside className="tpcrm-guide">
            <h4 className="tpcrm-guide-title">How the grade is worked out</h4>
            <p className="tpcrm-guide-lede">
                A vendor's grade really comes down to two things: how sensitive the
                data they touch is, and how they hold up in the security review.
            </p>

            <p className="tpcrm-guide-copy">The data and access you note here set the starting point.</p>
            <div className="tpcrm-guide-tiers">
                {SENSITIVITY_TIERS.map(({ tier, desc }) => {
                    const tone = criticalityTone(tier);
                    return (
                        <div className="tpcrm-guide-tier" key={tier}>
                            <span className="tpcrm-guide-chip" style={{ background: tone.background, color: tone.color }}>{tier}</span>
                            <span className="tpcrm-guide-tier-fields">{desc}</span>
                        </div>
                    );
                })}
            </div>
            <p className="tpcrm-guide-copy">
                The service matters too. Payment, cloud (AWS, Azure), managed IT and
                SOC start higher up; collaboration or marketing tools start lower.
            </p>

            <p className="tpcrm-guide-copy">Then the assessment score decides where it settles.</p>
            <div className="tpcrm-guide-matrix">
                <span className="tpcrm-guide-corner" />
                {MATRIX_GRADE.map((g) => (
                    <span className="tpcrm-guide-col-head" key={g} style={{ color: GRADE_COLORS[g] }}>{g}</span>
                ))}
                {MATRIX_CRIT.map((crit) => (
                    <React.Fragment key={crit}>
                        <span className="tpcrm-guide-row-head" style={{ color: criticalityTone(crit).color }}>{crit}</span>
                        {MATRIX_GRADE.map((g) => {
                            const tone = criticalityTone(cellTier(crit, g));
                            return (
                                <span
                                    className="tpcrm-guide-cell"
                                    key={g}
                                    style={{ background: tone.background, borderColor: tone.color }}
                                    title={`${crit} × Grade ${g} — ${cellTier(crit, g)} risk`}
                                />
                            );
                        })}
                    </React.Fragment>
                ))}
            </div>
            <div className="tpcrm-guide-axis-x">Assessment grade →</div>
            <div className="tpcrm-guide-legend">
                {RISK_BANDS.map((band) => {
                    const tone = criticalityTone(band);
                    return (
                        <span className="tpcrm-guide-legend-item" key={band}>
                            <span className="tpcrm-guide-legend-swatch" style={{ background: tone.background, borderColor: tone.color }} />
                            {band}
                        </span>
                    );
                })}
            </div>

            <p className="tpcrm-guide-foot">
                Whatever ends up on the vendor is the real assessment result. This is
                just here to show how the pieces fit.
            </p>
        </aside>
    );
}

// Create-only (the "+ Add Vendor" → Manual Create flow on /tpcrm/vendors).
// Editing an existing vendor happens inline in VendorProfileModal's Overview
// tab, not here — same field set/order as that form (and the old dashboard's
// AddVendor.js, left untouched there since it's still shared with still-dark
// pages), via the shared option lists in tpcrmUtils.js.
function AddVendorModal({ show, onHide, onSuccess }) {
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);

    const handleHide = () => {
        setError('');
        onHide?.();
    };

    const handleFormSubmit = async (data) => {
        setLoading(true);
        setError('');

        const params = {
            vendor_name: data.vendorName.trim(),
            vendor_domain: data.vendorDomain.trim(),
            vendor_email: data.vendorEmail.trim(),
            service_provided: data.serviceProvided,
            credential_access: data.credentialAccess,
            customer_personal_data: data.customerPersonalData,
            customer_sensitive_personal_data: data.customerSensitivePersonalData,
            customer_financial_data: data.customerFinancialData,
            customer_health_data: data.customerHealthData,
            customer_auth_data: data.customerAuthData,
            business_confidential_data: data.businessConfidentialData,
            vendor_category: data.vendorCategory === 'Auto' ? null : data.vendorCategory || null,
            internal_owner: data.internalOwner.trim() || null,
        };

        try {
            const response = await add_new_vendor_name(params);
            const errorMsg = response?.statusCode?.error?.message;
            const responseMsg = response?.statusCode?.response_data?.message;
            const newVendor = response?.statusCode?.response_data?.data;

            if (errorMsg) {
                setError(errorMsg);
            } else if (responseMsg && newVendor) {
                onSuccess?.(newVendor);
            } else {
                setError('Something went wrong, please try again.');
            }
        } catch (err) {
            console.error('Failed to create vendor:', err);
            setError('Something went wrong, please try again.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <CommonModal
            show={show}
            onHide={handleHide}
            title="Add Vendor"
            error={error}
            size="lg"
            fields={FIELDS}
            defaultValues={DEFAULT_VALUES}
            onSubmit={handleFormSubmit}
            buttons={[
                { text: 'Cancel', variant: 'secondary', onClick: handleHide, disabled: loading },
                { text: 'Add Vendor', variant: 'primary', type: 'submit', loading, loadingText: 'Saving…' },
            ]}
        >
            <VendorGradingGuide />
        </CommonModal>
    );
}

export default AddVendorModal;
